import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import * as R from "ramda";
import { useLocation } from "react-router-dom-v5-compat";

import { FullScreenLoader } from "components/Loader";
import { RequirePrivilegedUser } from "components/utilities/guards";
import { useTenant } from "queries/tenant";
import { useVersions } from "queries/versions";
import { getTimezone } from "reducers/tenant";
import { Version } from "types/entities";
import { getParam } from "utils/urls";

import { CurrentTenantContext } from "./currentTenant";
import { AwaitSessionLoaded } from "./session";

type VersionNumber = number | "current" | "head";

interface CurrentTenantProviderProps {
  tenantID: number;
  tenantVersion: number;
  children: React.ReactNode;
  locked?: boolean;
  defaultToCurrent?: boolean;
  versions: Version[];
}

const CurrentTenantProvider = ({
  tenantID,
  tenantVersion: initialTenantVersion,
  children,
  locked = false,
  defaultToCurrent = true,
  versions: initialVersions,
}: CurrentTenantProviderProps) => {
  const [tenantVersion, setTenantVersion] = useState(initialTenantVersion);
  const [_versionLocked, setVersionLocked] = useState(false);
  const [versionChanged, setVersionChanged] = useState(false);
  const versionLocked = locked || _versionLocked;

  const {
    data: tenant,
    isLoading: isInitialLoading,
    isPreviousData,
  } = useTenant(tenantID, tenantVersion);

  const timeZone = getTimezone(tenant);
  useEffect(() => {
    dayjs.tz.setDefault(timeZone);
  }, [timeZone]);

  const isLoading = isInitialLoading || isPreviousData;
  const { data: versions } = useVersions({
    initialData: initialVersions,
  });
  const version = useMemo(
    () => R.find(R.propEq(tenantVersion, "number"), versions),
    [versions, tenantVersion],
  ) as Version;
  const currentVersion = useMemo(
    () => R.prop("number", R.find(R.propEq(true, "current"), versions)),
    [versions],
  );
  const headVersion = useMemo(
    () => R.prop("number", R.find(R.propEq(true, "head?"), versions as any)),
    [versions],
  );
  const getVersionNumber = useCallback(
    (versionNumberOrState: VersionNumber) => {
      if (R.isNil(versionNumberOrState)) throw new Error("Invalid version number: null");
      if (R.is(Number, versionNumberOrState)) return versionNumberOrState;
      const normalizedInput = versionNumberOrState.toLowerCase();
      const parsedNumber = Number(versionNumberOrState);
      if (!Number.isNaN(parsedNumber) && parsedNumber > 0) {
        return parsedNumber;
      }

      switch (normalizedInput) {
        case "current":
          return currentVersion;
        case "head":
          return headVersion;
        default:
          throw new Error(`Invalid version number: ${versionNumberOrState}`);
      }
    },
    [currentVersion, headVersion],
  );

  const isCurrentVersion = version?.current;

  const onChangeVersion = useCallback(
    (version_number: VersionNumber) => {
      if (versionLocked) return;

      setVersionChanged(true);
      setTenantVersion(getVersionNumber(version_number));
    },
    [versionLocked, getVersionNumber],
  );
  const onLockVersion = useCallback(
    (lockedVersion: VersionNumber) => {
      if (lockedVersion) {
        setVersionLocked(true);
        setTenantVersion(getVersionNumber(lockedVersion));
      } else {
        setVersionLocked(false);
      }
    },
    [setTenantVersion, getVersionNumber],
  );

  const locationStateVersion = useLocation()?.state?.version;
  const location = useLocation();
  const versionParamFromURL = getParam(location, "version") as VersionNumber;
  useEffect(() => {
    if (isLoading) return;

    if (versionParamFromURL) {
      onChangeVersion(getVersionNumber(versionParamFromURL));
      return;
    }
    if (locationStateVersion) {
      onChangeVersion(getVersionNumber(locationStateVersion));
      return;
    }
    if (isCurrentVersion || versionChanged) return;
    if (defaultToCurrent && currentVersion) {
      onChangeVersion(currentVersion);
    }
  }, [
    currentVersion,
    defaultToCurrent,
    getVersionNumber,
    isCurrentVersion,
    isLoading,
    locationStateVersion,
    onChangeVersion,
    versionChanged,
    versionParamFromURL,
  ]);

  const contextValue = useMemo(
    () => ({
      tenant,
      isLoading,
      onChangeVersion,
      onLockVersion,
    }),
    [tenant, isLoading, onChangeVersion, onLockVersion],
  );

  return (
    <CurrentTenantContext.Provider value={contextValue}>
      {tenant && children}
      {isLoading && <FullScreenLoader label="Loading Tenant" overlay />}
    </CurrentTenantContext.Provider>
  );
};

interface TenantProviderWithPossibleAuthProps extends CurrentTenantProviderProps {
  requiresAuth: boolean;
}
const TenantProviderWithPossibleAuth = ({
  requiresAuth,
  ...props
}: TenantProviderWithPossibleAuthProps) => {
  if (requiresAuth) {
    return (
      <AwaitSessionLoaded>
        <RequirePrivilegedUser>
          <CurrentTenantProvider {...props} />;
        </RequirePrivilegedUser>
      </AwaitSessionLoaded>
    );
  }
  return <CurrentTenantProvider {...props} />;
};

export default TenantProviderWithPossibleAuth;
